/** Right-click menu items for the sectioned rail's rows, section headers and spaces (spec §2.3). */
import { destinationById } from '@shared/navigation/destinations';
import { idOf, type RailLayout } from '@shared/navigation/railLayout';
import {
  canIndent,
  canMoveEntry,
  canMoveItem,
  indent,
  moveEntry,
  moveItem,
  outdent,
  parentOf,
  removeSection,
  removeSpace,
  rowAbove,
} from '@shared/navigation/railLayoutOps';

import type { ContextMenuItem } from '@features/workspace';

/** What a menu action can do to the rail. */
export interface RailActions {
  /** Write a whole new layout. */
  commit: (next: RailLayout) => void;
  /** Unstar a destination. */
  unpin: (id: string) => void;
  /** Add a section (above a row, or empty at the end) and open it for naming. */
  startSection: (beforeId?: string) => void;
  /** Add a space (above a row, or at the end). */
  addSpace: (beforeId?: string) => void;
  /** Open a section header for renaming. */
  startRename: (id: string) => void;
  /** Turn edit mode on. */
  startEditing: () => void;
}

/** What a menu needs to know about the rail it opens on. */
export interface RailMenuState {
  editMode: boolean;
  isCollapsed: boolean;
}

/** A destination's label, for menu text. */
function labelOf(id: string): string {
  return destinationById(id)?.label ?? id;
}

/**
 * *Edit sidebar*, last in every rail menu, when edit mode is off on an expanded
 * rail. The menu is the only way in: sections are made once or twice a year, so
 * the rail carries no permanent control for it. A collapsed rail has nowhere to
 * show edit mode, so it offers none.
 */
function editItems(
  { editMode, isCollapsed }: RailMenuState,
  actions: RailActions
): ContextMenuItem[] {
  return editMode || isCollapsed ? [] : [{ label: 'Edit sidebar', onClick: actions.startEditing }];
}

/**
 * Structure edits for a row, offered in edit mode on an expanded rail. A sub-item
 * can only move back out; any other row can start a section or a space above
 * itself, and — when the row above is a row — become its sub-item.
 */
function structureItems(layout: RailLayout, id: string, actions: RailActions): ContextMenuItem[] {
  const parent = parentOf(layout, id);
  if (parent !== undefined) {
    return [
      {
        label: `Move out of ${labelOf(parent)}`,
        onClick: () => actions.commit(outdent(layout, id)),
      },
    ];
  }
  const above = rowAbove(layout, id);
  return [
    { label: 'Add section above', onClick: () => actions.startSection(id) },
    { label: 'Add space above', onClick: () => actions.addSpace(id) },
    ...(above !== undefined && canIndent(layout, id)
      ? [
          {
            label: `Make sub-item of ${labelOf(idOf(above))}`,
            onClick: () => actions.commit(indent(layout, id)),
          },
        ]
      : []),
  ];
}

/** A starred row's items. */
export function rowMenuItems(
  layout: RailLayout,
  id: string,
  state: RailMenuState,
  actions: RailActions
): ContextMenuItem[] {
  return [
    {
      label: 'Move up',
      onClick: () => actions.commit(moveEntry(layout, id, -1)),
      disabled: !canMoveEntry(layout, id, -1),
    },
    {
      label: 'Move down',
      onClick: () => actions.commit(moveEntry(layout, id, 1)),
      disabled: !canMoveEntry(layout, id, 1),
    },
    ...(state.editMode && !state.isCollapsed ? structureItems(layout, id, actions) : []),
    { label: 'Unpin from sidebar', onClick: () => actions.unpin(id) },
    ...editItems(state, actions),
  ];
}

/** Move up / Move down for a top-level item, labelled for its kind. */
function topLevelMoves(
  layout: RailLayout,
  id: string,
  noun: string,
  actions: RailActions
): ContextMenuItem[] {
  return [
    {
      label: `Move ${noun} up`,
      onClick: () => actions.commit(moveItem(layout, id, -1)),
      disabled: !canMoveItem(layout, id, -1),
    },
    {
      label: `Move ${noun} down`,
      onClick: () => actions.commit(moveItem(layout, id, 1)),
      disabled: !canMoveItem(layout, id, 1),
    },
  ];
}

/** A section header's items. Moves step past one neighbour: a row, a space, or a whole section. */
export function sectionMenuItems(
  layout: RailLayout,
  id: string,
  state: RailMenuState,
  actions: RailActions
): ContextMenuItem[] {
  return [
    { label: 'Rename', onClick: () => actions.startRename(id) },
    ...topLevelMoves(layout, id, 'section', actions),
    { label: 'Remove section', onClick: () => actions.commit(removeSection(layout, id)) },
    ...editItems(state, actions),
  ];
}

/** A space's items (it has a menu only in edit mode). */
export function spaceMenuItems(
  layout: RailLayout,
  id: string,
  actions: RailActions
): ContextMenuItem[] {
  return [
    ...topLevelMoves(layout, id, 'space', actions),
    { label: 'Remove space', onClick: () => actions.commit(removeSpace(layout, id)) },
  ];
}
